import axios, { AxiosInstance } from "axios"

const ovokBaseUrl: string =
    process.env.REACT_APP_OVOK_API_URL || "https://api.ovok.com/"

export interface LoginStartResponse {
    nextStep: "mfa" | "token"
    loginId?: string
    sessionCode?: string
    profiles?: Array<{
        tenantCode: string
        project: {
            id: string
            resourceType: string
            name: string
        }
        profile: {
            resourceType: string
            id: string
            meta: {
                project: string
                versionId: string
                lastUpdated: string
                author: { reference: string }
                compartment: Array<{ reference: string }>
            }
            name: Array<{ given: string[]; family: string }>
            telecom: Array<{ system: string; use: string; value: string }>
        }
    }>
}

export interface LoginMfaResponse {
    nextStep: "token"
    sessionCode: string
    profiles: Array<{
        tenantCode: string
        project: {
            id: string
            resourceType: string
            name: string
        }
        profile: {
            resourceType: string
            id: string
            meta: {
                project: string
                versionId: string
                lastUpdated: string
                author: { reference: string }
                compartment: Array<{ reference: string }>
            }
            name: Array<{ given: string[]; family: string }>
            telecom: Array<{ system: string; use: string; value: string }>
        }
    }>
}

export interface LoginTokenResponse {
    accessToken: string
    refreshToken: string
    project: {
        reference: string
        display: string
    }
    profile: {
        reference: string
        display: string
    }
    expiresIn: number
}

export interface LoginCompleteResponse {
    accessToken: string
    refreshToken: string
}

interface ApiError {
    statusCode: number
    error: string
    timestamp: string
    path: string
    message: string
    requestId: string
}

export const useApiClient = (token: string) => {
    const apiClient = axios.create({
        baseURL: "https://api.ovok.com/",
    })

    apiClient.interceptors.request.use(
        async (config) => {
            if (token) {
                config.headers.Authorization = `Bearer ${token}`
            }
            return config
        },
        (error) => {
            return Promise.reject(error)
        }
    )

    return apiClient
}

export const startLogin = async (
    email: string,
    password: string,
    codeChallenge: string
): Promise<LoginStartResponse> => {
    const response = await fetch(
        `${ovokBaseUrl}auth/tenant/Practitioner/login/start`,
        {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                accept: "application/json",
            },
            body: JSON.stringify({
                email,
                password,
                codeChallenge,
            }),
        }
    )

    if (!response.ok) {
        const error: ApiError = await response.json()
        throw new Error(error.message)
    }

    return response.json()
}

export const submitMfa = async (
    mfaToken: string,
    loginId: string
): Promise<LoginMfaResponse> => {
    const response = await fetch(
        `${ovokBaseUrl}auth/tenant/Practitioner/login/mfa`,
        {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                accept: "application/json",
            },
            body: JSON.stringify({
                mfaToken,
                loginId,
            }),
        }
    )

    if (!response.ok) {
        const error: ApiError = await response.json()
        throw new Error(error.message)
    }

    return response.json()
}

export const getLoginToken = async (
    sessionCode: string,
    codeVerifier: string,
    tenantCode: string
): Promise<LoginTokenResponse> => {
    const response = await axios.post<LoginTokenResponse>(
        `${ovokBaseUrl}auth/tenant/Practitioner/login/token`,
        {
            sessionCode,
            codeVerifier,
            tenantCode,
        },
        {
            headers: {
                accept: "application/json",
                "content-type": "application/json",
            },
        }
    )
    return response.data
}

export const completeLogin = async (
    sessionCode: string,
    codeVerifier: string,
    tenantCode: string
): Promise<LoginTokenResponse> => {
    const response = await fetch(
        `${ovokBaseUrl}auth/tenant/Practitioner/login/token`,
        {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                accept: "application/json",
            },
            body: JSON.stringify({
                sessionCode,
                codeVerifier,
                tenantCode,
            }),
        }
    )

    if (!response.ok) {
        const error: ApiError = await response.json()
        throw new Error(error.message)
    }

    return response.json()
}
